import React from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { cn } from '../../../utils/cn';

interface ConversationLayoutProps {
  leftPanel: React.ReactNode;
  centerPanel: React.ReactNode;
  rightPanel: React.ReactNode;
  className?: string;
}

/**
 * Premium ConversationLayout - Inspired by Linear, PostHog, Stripe
 * 
 * Features:
 * - Glass morphism effects
 * - Premium shadows and borders
 * - Smooth panel transitions
 * - Perfect spacing and proportions
 * - Sophisticated visual hierarchy
 * - Premium color palette
 */
export const ConversationLayout: React.FC<ConversationLayoutProps> = ({
  leftPanel,
  centerPanel,
  rightPanel,
  className
}) => {
  return (
    <div className={cn(
      // Container styling
      "h-full w-full",
      "bg-gradient-to-br from-neutral-25 via-neutral-50 to-neutral-100",
      "relative overflow-hidden",
      className
    )}>
      {/* Premium Background Pattern */}
      <div className="absolute inset-0 bg-[radial-gradient(circle_at_1px_1px,rgb(0_0_0/0.025)_1px,transparent_0)] [background-size:24px_24px]" />
      
      {/* Main Layout Grid */}
      <div className="relative h-full grid grid-cols-12 gap-6 p-6">
        
        {/* Left Panel - Navigator */}
        <motion.div
          initial={{ opacity: 0, x: -20 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ duration: 0.3, ease: "easeOut" }}
          className="col-span-3 h-full"
        >
          <div className={cn(
            // Premium container styling
            "h-full rounded-xl",
            "bg-white/90 backdrop-blur-xl",
            "border border-neutral-200/60",
            "shadow-[0_8px_16px_rgba(0,0,0,0.04)]",
            "ring-1 ring-black/[0.02]",
            
            // Hover effects
            "transition-all duration-200",
            "hover:shadow-[0_12px_24px_rgba(0,0,0,0.06)]",
            "hover:ring-black/[0.04]",
            
            // Inner styling
            "overflow-hidden"
          )}>
            {leftPanel}
          </div>
        </motion.div>

        {/* Center Panel - Conversation Stream */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.3, ease: "easeOut", delay: 0.1 }}
          className="col-span-6 h-full"
        >
          <div className={cn(
            // Premium container styling
            "h-full rounded-xl",
            "bg-white/95 backdrop-blur-xl",
            "border border-neutral-200/60",
            "shadow-[0_8px_20px_rgba(0,0,0,0.06)]",
            "ring-1 ring-black/[0.02]",
            
            // Enhanced center panel styling
            "relative",
            
            // Hover effects
            "transition-all duration-200",
            "hover:shadow-[0_16px_32px_rgba(0,0,0,0.08)]",
            "hover:ring-black/[0.04]",
            
            // Inner styling
            "overflow-hidden"
          )}>
            {/* Subtle inner glow */}
            <div className="absolute inset-0 bg-gradient-to-br from-primary-50/30 via-transparent to-success-50/20 pointer-events-none" />
            
            <div className="relative h-full">
              {centerPanel}
            </div>
          </div>
        </motion.div>

        {/* Right Panel - Intelligence Hub */}
        <motion.div
          initial={{ opacity: 0, x: 20 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ duration: 0.3, ease: "easeOut", delay: 0.2 }}
          className="col-span-3 h-full"
        >
          <div className={cn(
            // Premium container styling
            "h-full rounded-xl",
            "bg-white/90 backdrop-blur-xl",
            "border border-neutral-200/60",
            "shadow-[0_8px_16px_rgba(0,0,0,0.04)]",
            "ring-1 ring-black/[0.02]",
            
            // Intelligence panel accent
            "relative",
            
            // Hover effects
            "transition-all duration-200",
            "hover:shadow-[0_12px_24px_rgba(0,0,0,0.06)]",
            "hover:ring-black/[0.04]",
            
            // Inner styling
            "overflow-hidden"
          )}>
            {/* AI Intelligence accent */}
            <div className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-primary-400/40 to-transparent" />
            
            {rightPanel}
          </div>
        </motion.div>
      </div>

      {/* Premium Ambient Effects */}
      <div className="absolute inset-0 pointer-events-none">
        {/* Subtle corner gradients */}
        <div className="absolute top-0 left-0 w-96 h-96 bg-gradient-radial from-primary-100/20 via-transparent to-transparent" />
        <div className="absolute bottom-0 right-0 w-96 h-96 bg-gradient-radial from-success-100/15 via-transparent to-transparent" />
      </div>
    </div>
  );
};

/**
 * Premium Panel Component for consistent panel styling
 */
interface PanelProps {
  children: React.ReactNode;
  className?: string;
  header?: React.ReactNode;
  footer?: React.ReactNode;
}

export const ConversationPanel: React.FC<PanelProps> = ({
  children,
  className,
  header,
  footer
}) => {
  return (
    <div className={cn("h-full flex flex-col", className)}>
      {header && (
        <div className={cn(
          "flex-shrink-0",
          "px-6 py-4",
          "border-b border-neutral-200/60",
          "bg-gradient-to-b from-neutral-50/80 to-transparent"
        )}>
          {header}
        </div>
      )}
      
      <div className="flex-1 overflow-hidden">
        {children}
      </div>
      
      {footer && (
        <div className={cn(
          "flex-shrink-0",
          "px-6 py-4",
          "border-t border-neutral-200/60",
          "bg-gradient-to-t from-neutral-50/80 to-transparent"
        )}>
          {footer}
        </div>
      )}
    </div>
  );
};

/**
 * Premium Panel Header
 */
interface PanelHeaderProps {
  children: React.ReactNode;
  className?: string;
  actions?: React.ReactNode;
}

export const PanelHeader: React.FC<PanelHeaderProps> = ({
  children,
  className,
  actions
}) => {
  return (
    <div className={cn(
      "flex items-center justify-between",
      "px-6 py-4",
      "border-b border-neutral-200/60",
      "bg-gradient-to-b from-neutral-50/80 to-transparent",
      "backdrop-blur-sm",
      className
    )}>
      <div className="flex-1 min-w-0">
        {children}
      </div>
      
      {actions && (
        <div className="flex-shrink-0 ml-4">
          {actions}
        </div>
      )}
    </div>
  );
};

/**
 * Premium Panel Content
 */
interface PanelContentProps {
  children: React.ReactNode;
  className?: string;
  padding?: 'none' | 'sm' | 'md' | 'lg';
}

export const PanelContent: React.FC<PanelContentProps> = ({
  children,
  className,
  padding = 'md'
}) => {
  const paddingClasses = {
    none: '',
    sm: 'p-3',
    md: 'p-6',
    lg: 'p-8'
  };

  return (
    <div className={cn(
      "h-full overflow-auto",
      paddingClasses[padding],
      className
    )}>
      {children}
    </div>
  );
};

/**
 * Premium Panel Footer
 */
interface PanelFooterProps {
  children: React.ReactNode;
  className?: string;
}

export const PanelFooter: React.FC<PanelFooterProps> = ({
  children,
  className
}) => {
  return (
    <div className={cn(
      "px-6 py-4",
      "border-t border-neutral-200/60",
      "bg-gradient-to-t from-neutral-50/80 to-transparent",
      "backdrop-blur-sm",
      className
    )}>
      {children}
    </div>
  );
}; 